import React from 'react';
import { T, FONT, SERIF, radius, shadow, tint } from './theme';
import { Icon } from './icons';
import { LiveDot } from './primitives';
import { useViewport } from './hooks';

export type NavItem = { key: string; label: string; icon?: keyof typeof Icon };

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: 'grid' },
  { key: 'optimizasyonlar', label: 'Optimizasyonlar' },
  { key: 'sablonlar', label: 'Şablonlar' },
  { key: 'raporlar', label: 'Raporlar' },
  { key: 'kreatif', label: 'Kreatif Stüdyo' },
  { key: 'feed', label: 'Feed Wizard' },
];

const RAIL: (keyof typeof Icon)[] = ['grid', 'chart', 'target', 'users', 'bolt', 'layers'];

// ═══════════════════════════════════════════════════════════════
//  AppShell — wrap EVERY page with this. Owns sidebar, topbar,
//  responsive collapse, scrolling content region, and footer.
//
//  <AppShell active="dashboard" nav={NAV} onNavigate={setPage}
//           user={{ name:'Zsa Zsa Zsu', role:'Müşteri hesabı', initial:'Z' }}
//           dateLabel="1 Ağu – 3 Ağu">
//    …page content…
//  </AppShell>
// ═══════════════════════════════════════════════════════════════
export function AppShell({ children, active = 'dashboard', nav = NAV, onNavigate,
  user = { name: 'Zsa Zsa Zsu', role: 'Müşteri hesabı', initial: 'Z' }, dateLabel = '1 Ağu – 3 Ağu' }:
  {
    children: React.ReactNode; active?: string; nav?: NavItem[]; onNavigate?: (k: string) => void;
    user?: { name: string; role: string; initial: string }; dateLabel?: string;
  }) {
  const { isMobile, isNarrow, w } = useViewport();
  // Nav collapses to the hamburger EARLIER than the sidebar: the full inline
  // nav is too wide to coexist with the account controls in the 820–1080 band.
  const navCollapsed = w < 1080;
  const [menuOpen, setMenuOpen] = React.useState(false);

  const railBtn = (name: keyof typeof Icon, on = false): React.CSSProperties => ({
    width: 44, height: 44, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: on ? T.accent : T.textDim, background: on ? tint(T.accent, 13) : 'transparent',
  });

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', background: T.bg, fontFamily: FONT, color: T.text, overflow: 'hidden', fontSize: 14 }}>

      {/* ── Icon rail (hidden on mobile) ── */}
      {!isMobile && (
        <aside style={{ width: 74, flexShrink: 0, background: T.surface, borderRight: `1px solid ${T.divider}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0 18px', gap: 6, zIndex: 2 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', border: `3.5px solid ${T.accent}`, marginBottom: 16, flexShrink: 0 }} />
          {RAIL.map((name, i) => {
            const Cmp = Icon[name];
            return <div key={name} style={railBtn(name, i === 0)}><Cmp size={21} /></div>;
          })}
          <div style={{ flex: 1 }} />
          <div style={railBtn('gear')}><Icon.gear size={21} /></div>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#2A2D48,#4A4E6D)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, marginTop: 4 }}>
            {user.initial}
          </div>
        </aside>
      )}

      {/* ── Main column ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* ── Topbar ── */}
        <header style={{ height: 64, flexShrink: 0, background: T.surface, borderBottom: `1px solid ${T.divider}`,
          display: 'flex', alignItems: 'center', gap: 'clamp(12px,2vw,28px)', padding: '0 clamp(14px,3vw,26px)', zIndex: 4, position: 'relative' }}>
          {navCollapsed && (
            <button onClick={() => setMenuOpen(o => !o)} style={{ width: 40, height: 40, borderRadius: radius.md,
              border: `1px solid ${T.borderMid}`, background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <Icon.menu size={20} color={T.textMid} />
            </button>
          )}
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em' }}>Poyais <span style={{ color: T.accent }}>One</span></div>

          {!navCollapsed && (
            <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {nav.map(n => {
                const on = n.key === active;
                return (
                  <span key={n.key} onClick={() => onNavigate?.(n.key)}
                    style={{ padding: '8px 14px', borderRadius: radius.md, fontSize: 13.5, fontWeight: on ? 700 : 600,
                      cursor: 'pointer', color: on ? T.accent : T.textMid, background: on ? tint(T.accent, 12) : 'transparent' }}>
                    {n.label}
                  </span>
                );
              })}
            </nav>
          )}

          <div style={{ flex: 1 }} />

          {w >= 720 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${T.borderMid}`, background: T.surface,
              borderRadius: radius.md, padding: '8px 13px', fontSize: 13, fontWeight: 600, color: T.textMid, cursor: 'pointer' }}>
              <Icon.calendar size={16} color={T.textDim} />{dateLabel}<Icon.chevron size={14} color={T.textDim} />
            </div>
          )}
          <div style={{ position: 'relative', width: 40, height: 40, borderRadius: radius.md, border: `1px solid ${T.borderMid}`,
            background: T.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Icon.bell size={19} color={T.textMid} />
            <span style={{ position: 'absolute', top: 9, right: 10, width: 7, height: 7, borderRadius: '50%', background: T.red, border: `1.5px solid ${T.surface}` }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg,${T.accent},${tint(T.accent, 55)})`,
              color: T.accentInk, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{user.initial}</div>
            {!isNarrow && (
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{user.name}</div>
                <div style={{ fontSize: 11, color: T.textDim }}>{user.role}</div>
              </div>
            )}
            <Icon.chevron size={15} color={T.textDim} />
          </div>

          {/* nav dropdown (shown whenever the hamburger is) */}
          {navCollapsed && menuOpen && (
            <div style={{ position: 'absolute', top: 64, left: 0, right: 0, background: T.surface, borderBottom: `1px solid ${T.divider}`,
              boxShadow: shadow.hover, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 3, zIndex: 5 }}>
              {nav.map(n => {
                const on = n.key === active;
                return (
                  <span key={n.key} onClick={() => { onNavigate?.(n.key); setMenuOpen(false); }}
                    style={{ padding: '11px 13px', borderRadius: radius.md, fontSize: 14, fontWeight: on ? 700 : 600,
                      color: on ? T.accent : T.textMid, background: on ? tint(T.accent, 12) : 'transparent' }}>
                    {n.label}
                  </span>
                );
              })}
            </div>
          )}
        </header>

        {/* ── Scrolling content ── */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '22px clamp(14px,3vw,30px) 52px' }}>
          <div style={{ maxWidth: 1380, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {children}
            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
}

// ── Page header (title + subtitle + actions) ───────────────────
export function PageHeader({ title, subtitle, right }:
  { title: string; subtitle?: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 'clamp(20px,2.4vw,24px)', fontWeight: 800, letterSpacing: '-0.025em', color: T.text }}>{title}</h1>
        {subtitle && <p style={{ margin: '7px 0 0', fontSize: 13.5, color: T.textMid }}>{subtitle}</p>}
      </div>
      {right && <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{right}</div>}
    </div>
  );
}

// ── Footer ─────────────────────────────────────────────────────
export function Footer() {
  return (
    <footer style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      marginTop: 6, paddingTop: 22, borderTop: `1px solid ${T.divider}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.16em', color: T.textDim }}>POYAIS DIGITAL</span>
        <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.borderSoft }} />
        <span style={{ fontSize: 12, color: T.textFaint }}>Poyais One v1.0</span>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700, color: T.greenText,
        background: tint(T.green, 10), padding: '6px 13px', borderRadius: radius.pill }}>
        <LiveDot />GA4 · Google Ads · Meta bağlı
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════
//  v2 sidebar/topbar pieces — used by App.tsx's own Sidebar/topbar,
//  which keeps its role-filtering/mobile-drawer/client-switcher state
//  and composes these purely-presentational building blocks around it.
//  264px fixed sidebar, 60px topbar, single show/hide toggle (no hover
//  animation — see poyais-ui/README.md v2 notes).
// ═══════════════════════════════════════════════════════════════

export function SidebarShell({ children, width = 264 }: { children: React.ReactNode; width?: number }) {
  return (
    <aside style={{ width, flexShrink: 0, background: T.surface, borderRight: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column', height: '100%' }}>
      {children}
    </aside>
  );
}

export function SidebarBrand() {
  return (
    <div style={{ padding: '18px 20px 12px', display: 'flex', alignItems: 'center', gap: 9 }}>
      <div style={{ width: 22, height: 22, borderRadius: 6, background: T.accent, transform: 'rotate(45deg)', flexShrink: 0 }} />
      <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>Poyais <span style={{ color: T.accent }}>One</span></span>
    </div>
  );
}

export function WorkspaceSwitcher({ label, onClick }: { label: string; onClick?: () => void }) {
  const [h, setH] = React.useState(false);
  return (
    <div style={{ padding: '0 14px 12px' }}>
      <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', border: `1px solid ${h ? T.borderSoft : T.borderMid}`,
          borderRadius: 12, cursor: 'pointer', background: h ? T.bg : T.surface }}>
        <div style={{ width: 24, height: 24, borderRadius: 7, background: tint(T.accent, 16), color: T.accentDeep,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
          {label[0]}
        </div>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em' }}>{label}</span>
        <Icon.chevronUpDown size={14} color={T.textMidAlt} />
      </div>
    </div>
  );
}

export function SidebarNav({ children }: { children: React.ReactNode }) {
  return (
    <nav style={{ flex: 1, overflowY: 'auto', padding: '2px 14px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {children}
    </nav>
  );
}

export function SidebarGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase' as const,
      color: T.textMidAlt, padding: '16px 11px 7px' }}>{children}</div>
  );
}

export function SidebarNavItem({ icon, label, active = false, onClick }:
  { icon?: keyof typeof Icon; label: string; active?: boolean; onClick?: () => void }) {
  const [h, setH] = React.useState(false);
  const Cmp = icon ? Icon[icon] : null;
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 10, cursor: 'pointer',
        fontSize: 13.5, fontWeight: active ? 700 : 600,
        color: active ? T.accentDeep : T.textMid,
        background: active ? tint(T.accent, 14) : (h ? 'color-mix(in srgb, ' + T.accent + ' 8%, ' + T.surface + ')' : 'transparent') }}>
      {Cmp && <Cmp size={18} sw={1.9} />}
      {label}
    </div>
  );
}

export function SidebarPlatformRow({ badgeBg, badgeColor, badgeLabel, label, onClick }:
  { badgeBg: string; badgeColor: string; badgeLabel: string; label: string; onClick?: () => void }) {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px', borderRadius: 10, cursor: 'pointer',
        fontSize: 13.5, fontWeight: 600, color: T.textMid,
        background: h ? 'color-mix(in srgb, ' + T.accent + ' 8%, ' + T.surface + ')' : 'transparent' }}>
      <span style={{ width: 20, height: 20, borderRadius: 6, background: badgeBg, color: badgeColor, fontSize: 10, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{badgeLabel}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <Icon.chevronRight size={14} color={T.textFaint} sw={2.2} />
    </div>
  );
}

export function InviteCard({ title = 'Ekibini davet et', text = 'Ekip arkadaşlarını ekle, raporları ve panoları birlikte yönetin.' }:
  { title?: string; text?: string }) {
  return (
    <div style={{ padding: '0 14px 12px' }}>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, padding: '15px 16px', background: T.bg }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: tint(T.accent, 14), color: T.accentDeep,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 11 }}>
          <Icon.send size={17} sw={1.9} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 5 }}>{title}</div>
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: T.textMidAlt }}>{text}</p>
      </div>
    </div>
  );
}

export function SidebarUserRow({ initials, name, email, onClick }:
  { initials: string; name: string; email: string; onClick?: () => void }) {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ borderTop: `1px solid ${T.tableDivider}`, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
        cursor: 'pointer', background: h ? T.subtleBg : 'transparent' }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#2A2D48,#4A4E6D)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{initials}</div>
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
        <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        <div style={{ fontSize: 11, color: T.textMidAlt, whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
      </div>
      <Icon.chevronUpDown size={14} color={T.textMidAlt} sw={2.2} />
    </div>
  );
}

// ── Topbar (60px) ────────────────────────────────────────────────
export function TopbarShell({ title, onToggleSidebar, right }:
  { title: string; onToggleSidebar?: () => void; right?: React.ReactNode }) {
  return (
    <header style={{ height: 60, flexShrink: 0, background: T.surface, borderBottom: `1px solid ${T.border}`,
      display: 'flex', alignItems: 'center', gap: 14, padding: '0 clamp(16px,2.4vw,28px)' }}>
      <button onClick={onToggleSidebar} style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: T.textMid }}
        onMouseEnter={e => (e.currentTarget.style.background = T.trackBg)}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
        <Icon.panelToggle size={19} sw={1.8} />
      </button>
      <div style={{ width: 1, height: 22, background: T.border }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', color: T.text }}>{title}</div>
      <div style={{ flex: 1 }} />
      {right}
    </header>
  );
}

export function TopbarBell({ onClick, hasDot = true }: { onClick?: () => void; hasDot?: boolean }) {
  return (
    <div onClick={onClick} style={{ position: 'relative', width: 34, height: 34, borderRadius: 9, display: 'flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      onMouseEnter={e => (e.currentTarget.style.background = T.trackBg)}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <Icon.bellAlert size={18} color={T.textMid} sw={1.8} />
      {hasDot && <span style={{ position: 'absolute', top: 7, right: 8, width: 6, height: 6, borderRadius: '50%',
        background: T.red, border: `1.5px solid ${T.surface}` }} />}
    </div>
  );
}

// ── Big serif screen header — used by the 7 v2 screens' own <h1> ──
export function ScreenHeader({ title, subtitle, right }:
  { title: string; subtitle?: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' as const }}>
      <div>
        <h1 style={{ margin: 0, fontFamily: SERIF, fontSize: 'clamp(30px,3.4vw,38px)', fontWeight: 400, letterSpacing: '-0.015em', lineHeight: 1.1, color: T.text }}>{title}</h1>
        {subtitle && <p style={{ margin: '9px 0 0', fontSize: 13.5, color: T.textMid }}>{subtitle}</p>}
      </div>
      {right && <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{right}</div>}
    </div>
  );
}
